import { Box, Flex, Progress, Text } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';

import { Button } from '../../../../../../components/ui/buttons/Button';
import { DASHBOARD } from '../../../../../../constants/urls';
import DashboardBackButton from '../../../DashboardBackButton';
import { PHOTOS_LIVE_VIEW_THRESHOLD } from '../../constants';

type PhotoUploadHeaderProps = {
  uploadedCount: number;
  isPostAcceptance?: boolean;
};

type StrengthTier = {
  label: string;
  fill: number;
  color: string;
  trackColor: string;
};

const getLiveStrengthTier = (count: number): StrengthTier => {
  if (count === 0) {
    return { label: 'Add photos', fill: 0, color: 'grayLight', trackColor: 'grayMediumBackground' };
  }
  if (count <= 2) {
    return {
      label: 'Getting started',
      fill: 25,
      color: 'leaseEndYellow',
      trackColor: 'grayMediumBackground',
    };
  }
  if (count <= 4) {
    return { label: 'Good', fill: 55, color: 'leaseEndYellow', trackColor: 'grayMediumBackground' };
  }
  if (count < PHOTOS_LIVE_VIEW_THRESHOLD) {
    return {
      label: 'Strong',
      fill: 80,
      color: 'leaseEndGreen',
      trackColor: 'grayMediumBackground',
    };
  }
  return {
    label: 'Excellent',
    fill: 100,
    color: 'leaseEndGreen',
    trackColor: 'grayMediumBackground',
  };
};

const DoneButton = () => {
  const history = useHistory();
  return (
    <Button
      onClick={() => history.push(DASHBOARD)}
      size="sm"
      bg="leaseEndGreen"
      color="white"
      borderRadius="full"
      fontWeight="700"
      px={5}
      flexShrink={0}
      _hover={{ bg: 'deepGreenCyanTurquoise' }}
      trackClick="marketplace_photos_done"
    >
      Done
    </Button>
  );
};

const PhotoUploadHeader = ({ uploadedCount, isPostAcceptance = false }: PhotoUploadHeaderProps) => {
  if (isPostAcceptance) {
    return (
      <Box>
        <DashboardBackButton />
        <Box maxW="900px" mx="auto" mt={4}>
          <Flex align="center" justify="space-between" gap={3}>
            <Box>
              <Text
                fontSize={{ base: 'xl', md: '2xl' }}
                fontWeight="800"
                color="leaseEndBlue"
                lineHeight="1.2"
              >
                Your photos
              </Text>
              <Text fontSize="sm" color="grayLight" mt={1}>
                Tap any photo to replace it, or add photos of your vehicle.
              </Text>
            </Box>
            <DoneButton />
          </Flex>
        </Box>
      </Box>
    );
  }

  const tier = getLiveStrengthTier(uploadedCount);
  const hasUpload = uploadedCount > 0;
  const reachedNudge = uploadedCount >= PHOTOS_LIVE_VIEW_THRESHOLD;
  const helperText = reachedNudge
    ? 'Great coverage - buyers can review your vehicle in full.'
    : `Add ${PHOTOS_LIVE_VIEW_THRESHOLD - uploadedCount} more for full coverage.`;

  const statusBg = hasUpload ? 'mintGreen' : 'iceBlue';
  const statusColor = hasUpload ? 'leaseEndGreen' : 'royalBlue';
  const statusText = reachedNudge
    ? 'Looking great - buyers can review your vehicle in full'
    : 'Add photos to strengthen your listing';

  return (
    <Box>
      <DashboardBackButton />
      <Box maxW="900px" mx="auto" mt={4}>
        <Text
          fontSize={{ base: 'xl', md: '2xl' }}
          fontWeight="800"
          color="leaseEndBlue"
          lineHeight="1.2"
        >
          Take a few photos
        </Text>
        <Text fontSize="sm" color="grayLight" mt={1}>
          {`The more photos you add, the more offers you'll get. Listings with ${PHOTOS_LIVE_VIEW_THRESHOLD}+ photos get offers faster.`}
        </Text>

        <Flex
          mt={3}
          align="center"
          justify="space-between"
          gap={3}
          bg={statusBg}
          borderRadius="full"
          pl="20px"
          pr={hasUpload ? '6px' : '20px'}
          py="8px"
        >
          <Text fontSize="12px" fontWeight="700" color={statusColor}>
            {statusText}
          </Text>
          {hasUpload ? <DoneButton /> : null}
        </Flex>

        <Box mt={4}>
          <Flex justify="space-between" mb={1.5}>
            <Text fontSize="xs" fontWeight="700" color="grayDark">
              Listing strength
            </Text>
            <Text fontSize="xs" fontWeight="700" color={tier.color}>
              {tier.label}
            </Text>
          </Flex>
          <Progress
            value={tier.fill}
            size="sm"
            borderRadius="full"
            bg={tier.trackColor}
            sx={{ '& > div': { backgroundColor: `var(--chakra-colors-${tier.color})` } }}
          />
          <Text fontSize="xs" color="grayLight" fontWeight="500" mt={1.5}>
            {helperText}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default PhotoUploadHeader;
